import { createRouter, createRoute, type AnyRoute } from "@tanstack/react-router";
import type { FC } from "react";
import { rootRoute } from "./routes/__root";
import { IndexRoute } from "./routes/index";

// Wave 9 closeout (22 route imports)
import { adminApiRoute } from "./routes/admin-api";
import { advertiserApiV2Route } from "./routes/advertiser-api-v2";
import { advertiserApprovalRoute } from "./routes/advertiser-approval";
import { advertiserCampaignUploadRoute } from "./routes/advertiser-campaign-upload";
import { advertiserInvoicesRoute } from "./routes/advertiser-invoices";
import { advertiserNotificationsRoute } from "./routes/advertiser-notifications";
import { advertiserPortalRoute } from "./routes/advertiser-portal";
import { advertiserPublisherStatsRoute } from "./routes/advertiser-publisher-stats";
import { advertiserRbacRoute } from "./routes/advertiser-rbac";
import { advertiserSubUsersRoute } from "./routes/advertiser-sub-users";
import { advertiserWidgetsRoute } from "./routes/advertiser-widgets";
import { affiliateGroupsRoute } from "./routes/affiliate-groups";
import { affiliateManagerRoute } from "./routes/affiliate-manager";
import { affiliateNotesRoute } from "./routes/affiliate-notes";
import { affiliateReferralProgramRoute } from "./routes/affiliate-referral-program";
import { affiliateTiersRoute } from "./routes/affiliate-tiers";
import { agencyRoute } from "./routes/agency";
import { androidAppLinksRoute } from "./routes/android-app-links";
import { androidSdkRoute } from "./routes/android-sdk";
import { apiTrackingRoute } from "./routes/api-tracking";
import { capUtilizationWidgetRoute } from "./routes/cap-utilization-widget";
import { capiEventQualityScoreMonitoringRoute } from "./routes/capi-event-quality-score-monitoring";
import BrowserTargetingPage from "./routes/campaigns.browser-targeting";
import DaypartingPage from "./routes/campaigns.dayparting";
import DeviceTargetingPage from "./routes/campaigns.device-targeting";
import FallbackPage from "./routes/campaigns.fallback";
import GeoTargetingPage from "./routes/campaigns.geo-targeting";
import CampaignsIndexPage from "./routes/campaigns.index";
import { CampaignsLanguageTargetingRoute as LanguageTargetingPage } from "./routes/campaigns.language-targeting";
import SpilloverPage from "./routes/campaigns.spillover";
import TemplatesPage from "./routes/campaigns.templates";
import TzSchedulingPage from "./routes/campaigns.tz-scheduling";
import WizardPage from "./routes/campaigns.wizard";
import CreativesIndexPage from "./routes/creatives.index";
import { CreativeSizeLimitRoute as CreativeSizeLimitPage } from "./routes/creatives.size-limit";
import CreativeTypesPage from "./routes/creatives.types";
import { FreeTrialRoute as FreeTrialPage } from "./routes/free-trial";
import PricingPage from "./routes/pricing";
import { CampaignsOsTargetingPage } from "./routes/campaigns.os-targeting";
import { BrandingPage } from "./routes/branding";
import { SmartlinksRouterPage } from "./routes/smartlinks.router";
import { SmartlinksAbRoute as SmartlinksAbPage } from "./routes/smartlinks.ab";
import { SmartlinksAutorankRoute as SmartlinksAutorankPage } from "./routes/smartlinks.autorank";
import { SmartlinksAnalyticsRouteComponent as SmartlinksAnalyticsPage } from "./routes/smartlinks.analytics";
import { CreatorsPromoRoute as CreatorsPromoPage } from "./routes/creators.promo";
import { AffiliatesApprovalsRoute as AffiliatesApprovalsPage } from "./routes/affiliates.approvals";
import { SmartlinksFraudRoute as SmartlinksFraudPage } from "./routes/smartlinks.fraud";
import { SmartlinksCapacityRouteComponent as SmartlinksCapacityPage } from "./routes/smartlinks.capacity";
import { InfluencersLinkPage } from "./routes/influencers.link";
import { AdvertiserPortalPage } from "./routes/advertisers.portal";
import { TrustCenterPage } from "./routes/trust";
import { SmartlinkTargetingRoute as SmartlinkTargetingPage } from "./routes/smartlinks.targeting";
import advertisersApprovalRoute from "./routes/advertisers.approval";
import advertisersCampaignUploadRoute from "./routes/advertisers.campaign-upload";
import advertisersCapsRoute from "./routes/advertisers.caps";
import advertisersCreativeUploadRoute from "./routes/advertisers.creative-upload";
import advertisersNotificationsRoute from "./routes/advertisers.notifications";
import advertisersPublisherStatsRoute from "./routes/advertisers.publisher-stats";
import advertisersWidgetsRoute from "./routes/advertisers.widgets";
import affiliatesGroupsRoute from "./routes/affiliates.groups";
import affiliatesManagerRoute from "./routes/affiliates.manager";
import affiliatesNotesRoute from "./routes/affiliates.notes";
import publishersApiRoute from "./routes/publishers.api";
import publishersTrackingDomainsRoute from "./routes/publishers.tracking-domains";
import publishersPayoutOverridesRoute from "./routes/publishers.payout-overrides";
import publishersCapsRoute from "./routes/publishers.caps";
import affiliatesTiersRoute from "./routes/affiliates.tiers";
import payoutsHoldRoute from "./routes/payouts.hold";
import advertisersSubUsersRoute from "./routes/advertisers.sub-users";
import advertisersInvoicesRoute from "./routes/advertisers.invoices";
import advertisersRbacRoute from "./routes/advertisers.rbac";
import conversionsScrubRoute from "./routes/conversions.scrub";
import { affiliatesReferralProgramRoute } from "./routes/affiliates.referral-program";
import { publishersApiToggleRoute } from "./routes/publishers.api-toggle";
import { trackingApiTrackingRoute } from "./routes/tracking.api-tracking";
import trackingCrossDeviceRoute from "./routes/tracking.cross-device";
import trackingEventsRoute from "./routes/tracking.events";
import { trackingFirstPartyCookieRoute } from "./routes/tracking.first-party-cookie";
import { trackingIframeGatewayRoute } from "./routes/tracking.iframe-gateway";
import trackingInvitationCodesRoute from "./routes/tracking.invitation-codes";
import { trackingJsPixelRoute } from "./routes/tracking.js-pixel";
import { trackingQrRoute } from "./routes/tracking.qr";
import trackingS2sPostbackRoute from "./routes/tracking.s2s-postback";
import trackingClickCountsRoute from "./routes/tracking.click-counts";
import igamingPaymentHoldRoute from "./routes/igaming.payment-hold";
import { trackingUniversalTagRoute } from "./routes/tracking.universal-tag";
import trackingCookielessFallbackRoute from "./routes/tracking.cookieless-fallback";
import trackingCouponsRoute from "./routes/tracking.coupons";
import igamingMultiLevelRoute from "./routes/igaming.multi-level";
import igamingPlayerSegmentationRoute from "./routes/igaming.player-segmentation";
import igamingSubAffiliateRoute from "./routes/igaming.sub-affiliate";
import trackingImpressionsRoute from "./routes/tracking.impressions";
import trackingAndroidAppLinksRoute from "./routes/tracking.android-app-links";
import trackingUniversalLinksRoute from "./routes/tracking.universal-links";
import trackingDeferredDeepLinkRoute from "./routes/tracking.deferred-deep-link";
import trackingPostbackApiRoute from "./routes/tracking.postback-api";
import { trackingMacrosRoute } from "./routes/tracking.macros";
import { settingsCustomLoginDomainRoute } from "./routes/settings.custom-login-domain";
import { settingsCustomTrackingDomainRoute } from "./routes/settings.custom-tracking-domain";
import { mountSettingsCustomPostbackDomainRoute } from "./routes/settings.custom-postback-domain";
import { trackingDeepLinkRoute } from "./routes/tracking.deep-link";
import { campaignsMultiGoalRoute } from "./routes/campaigns.multi-goal";
import { settingsAdminApiRoute } from "./routes/settings.admin-api";
import { trackingSdkCustomParamsRoute } from "./routes/tracking.sdk-custom-params";
import { settingsAdvertiserApiRoute } from "./routes/settings.advertiser-api";
import { developersSdkDocsRoute } from "./routes/developers.sdk-docs";
import { trackingSdkEventsRoute } from "./routes/tracking.sdk-events";
import { settingsPublisherApiRoute } from "./routes/settings.publisher-api";
import { settingsSubnetworkFaviconRoute } from "./routes/settings.subnetwork-favicon";
import { settingsWebhooksRoute } from "./routes/settings.webhooks";
import { trackingWebToAppRoute } from "./routes/tracking.web-to-app";
import { trackingAndroidSdkRoute } from "./routes/tracking.android-sdk";
import { trackingIosSdkRoute } from "./routes/tracking.ios-sdk";
import { offerCheckerRoute } from "./routes/offer-checker";
import { linkTesterRoute } from "./routes/link-tester";
import { campaignGroupingRoute } from "./routes/campaign-grouping";
import { campaignCloneRoute } from "./routes/campaign-clone";
import { publisherCapsRoute } from "./routes/publisher-caps";
import { conversionScrubRoute } from "./routes/conversion-scrub";
import { affiliateManagerAssignmentRoute } from "./routes/affiliate-manager-assignment";
import { couponTrackingRoute } from "./routes/coupon-tracking";
import { clickLogReportRoute } from "./routes/click-log-report";
import { publisherPerformanceReportRoute } from "./routes/publisher-performance-report";
import { reportExportRoute } from "./routes/report-export";
import { publisherPayoutOverridesRoute } from "./routes/publisher-payout-overrides";
import { referralCodeTrackingRoute } from "./routes/referral-code-tracking";
import { guidedOnboardingRoute } from "./routes/guided-onboarding";
import { customFraudRuleEnginePerCampaignPerPartnerRoute } from "./routes/custom-fraud-rule-engine-per-campaign-per-partner";
import { budgetPacingAlertsRoute } from "./routes/budget-pacing-alerts";
import { campaignTzSchedulingRoute } from "./routes/campaign-tz-scheduling";
import { savedReportTemplatesRoute } from "./routes/saved-report-templates";
import { sftpScheduledExportRoute } from "./routes/sftp-scheduled-export";
import { igamingScheduledBiWarehouseExportsEtlRoute } from "./routes/igaming-scheduled-bi-warehouse-exports-etl";
import { automationCrudPermissionScopeRoute } from "./routes/automation-crud-permission-scope";
import { logLevelDataAccessRoute } from "./routes/log-level-data-access";
import { advertiserPayoutRulesRoute } from "./routes/advertiser-payout-rules";
import { metricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitRoute } from "./routes/metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit";
import { advertiserSignupRoute } from "./routes/advertiser-signup";
import { advertiserBillingRoute } from "./routes/advertiser-billing";
import { multiCurrencyRoute } from "./routes/multi-currency";
import { topAdvertisersWidgetRoute } from "./routes/top-advertisers-widget";
import { topPublishersWidgetRoute } from "./routes/top-publishers-widget";
import { campaignReadEndpointRoute } from "./routes/campaign-read-endpoint";
import { conversionPullEndpointRoute } from "./routes/conversion-pull-endpoint";
import { offerPreviewApiRoute } from "./routes/offer-preview-api";
import { clickLevelDetailReportsRoute } from "./routes/click-level-detail-reports";
import { goalReportsRoute } from "./routes/goal-reports";
import { globalGoalTrackingRoute } from "./routes/global-goal-tracking";
import { goalProgressWidgetRoute } from "./routes/goal-progress-widget";
import { fraudReportsMostFraudulentCampaignsPublishersRoute } from "./routes/fraud-reports-most-fraudulent-campaigns-publishers";
import { realTimeClickFraudDetectionRoute } from "./routes/real-time-click-fraud-detection";
import { realTimeConversionFraudDetectionRoute } from "./routes/real-time-conversion-fraud-detection";
import { landingPagesRoute } from "./routes/landing-pages";
import { commissionPreviewRoute } from "./routes/commission-preview";
import { campaignArchiveRoute } from "./routes/campaign-archive";
import { lifetimeCommissionRoute } from "./routes/lifetime-commission";
import { customAttributionWindowsRoute } from "./routes/custom-attribution-windows";
import { advertiserManagerRoute } from "./routes/advertiser-manager";
// Wave 7 (21 routes)
import { abnormalPatternMlRoute } from "./routes/abnormal-pattern-ml";
import { adjustPostbackTemplateRoute } from "./routes/adjust-postback-template";
import { admitadIntegrationRoute } from "./routes/admitad-integration";
import { advertiserCommissionRoute } from "./routes/advertiser-commission";
import { advertiserMacrosRoute } from "./routes/advertiser-macros";
import { aiPoweredFraudAlgorithmsRoute } from "./routes/ai-powered-fraud-algorithms";
import { allowedTrafficRoute } from "./routes/allowed-traffic";
import { amazonAdvertisingIntegrationRoute } from "./routes/amazon-advertising-integration";
import { anomalyAlertsMlDrivenUnusualActivityPatternsRoute } from "./routes/anomaly-alerts-ml-driven-unusual-activity-patterns";
import { anonymizerTorBlockRoute } from "./routes/anonymizer-tor-block";
import { anuraIntegrationRoute } from "./routes/anura-integration";
import { apiKeyGenerationPermissionPerRoleRoute } from "./routes/api-key-generation-permission-per-role";
import { apiKeyHeaderAuthRoute } from "./routes/api-key-header-auth";
import { apiRateLimitingRoute } from "./routes/api-rate-limiting";
import { applovinIntegrationRoute } from "./routes/applovin-integration";
import { appmetricaIntegrationRoute } from "./routes/appmetrica-integration";
import { appsflyerIntegrationMmpBridgeRoute } from "./routes/appsflyer-integration-mmp-bridge";
import { SupportSlaRoute } from "./routes/24-7-tier-blind-support-with-published-sla";
import { advertiserDashboardSeparatePersonaRoute } from "./routes/advertiser-dashboard-separate-persona";
import { apiDrivenReportPullForCustomBiDashboardsRoute } from "./routes/api-driven-report-pull-for-custom-bi-dashboards";
// Wave 7c (4 fix routes)
import { advancedRetargetingControlsForSmarterComplianceRoute } from "./routes/advanced-retargeting-controls-for-smarter-compliance";
import { advertiserApiRoute } from "./routes/advertiser-api";
import { agencyDashboardAggregatingAllClientPerformancRoute } from "./routes/agency-dashboard-aggregating-all-client-performanc";
import { agentDrivenCampaignOptimizationAiNativeRoute } from "./routes/agent-driven-campaign-optimization-ai-native";
// Wave 8 (23 routes)
import { appsflyerNetworkPostbackInstallsInAppEventsRoute } from "./routes/appsflyer-network-postback-installs-in-app-events";
import { apptrovePricingRoute } from "./routes/apptrove-pricing";
import { auditTrailSecurityComplianceLogRoute } from "./routes/audit-trail-security-compliance-log";
import { autoActionBlockMarkRejectAlertOnFraudRoute } from "./routes/auto-action-block-mark-reject-alert-on-fraud";
import { autoAlertAffiliateViaEmailInAppRoute } from "./routes/auto-alert-affiliate-via-email-in-app";
import { autoAlertPublisherManagerEmailRoute } from "./routes/auto-alert-publisher-manager-email";
import { autoBlockPublisherWhenFraudThresholdHitRoute } from "./routes/auto-block-publisher-when-fraud-threshold-hit";
import { autoDebitBillingStripeCardOnFileForPlatformFeesRoute } from "./routes/auto-debit-billing-stripe-card-on-file-for-platform-fees";
import { autoPauseCampaignWhenThresholdHitRoute } from "./routes/auto-pause-campaign-when-threshold-hit";
import { automatedInvoiceGenerationPublisherAdvertiserRoute } from "./routes/automated-invoice-generation-publisher-advertiser";
import { automaticLetSEncryptRenewalNotManualTicketRoute } from "./routes/automatic-let-s-encrypt-renewal-not-manual-ticket";
import { awinIntegrationApiKeyPublisherIdRoute } from "./routes/awin-integration-api-key-publisher-id";
import { awsS3DataExportIntegrationRoute } from "./routes/aws-s3-data-export-integration";
import { awsS3ExportScheduledClickConversionLogsRoute } from "./routes/aws-s3-export-scheduled-click-conversion-logs";
import { behavioralAnomalyDetectionAbnormalUserActionsRoute } from "./routes/behavioral-anomaly-detection-abnormal-user-actions";
import { bigcommerceIntegrationViaWebhookRoute } from "./routes/bigcommerce-integration-via-webhook";
import { blacklistIpBlockAnonymizerBlockRoute } from "./routes/blacklist-ip-block-anonymizer-block";
import { bonusStructureDisplayForPartnersRoute } from "./routes/bonus-structure-display-for-partners";
import { botTrafficDetectionRoute } from "./routes/bot-traffic-detection";
import { branchNetworkPartnerPostbackRoute } from "./routes/branch-network-partner-postback";
import { brandedPartnerPortalPerSubNetworkRoute } from "./routes/branded-partner-portal-per-sub-network";
import { brazeEventSyncIntegrationRoute } from "./routes/braze-event-sync-integration";
import { brazeIterableCustomerIoIntegrationsRoute } from "./routes/braze-iterable-customer-io-integrations";
// Wave 9 (20 backlog-wire routes)
// Wave 9 closeout-2 (19 route imports; 5 dupes skipped: creative-size-limit, creative-types, cookieless-fallback, ccpa-dsr, creator-drilldown)
import { capiTestEventModeForVerificationRoute } from "./routes/capi-test-event-mode-for-verification";
import { ccSubscribeRoute } from "./routes/cc-subscribe";
import { chartboostIntegrationRoute } from "./routes/chartboost-integration";
import { clevertap2WayApptroveNativeSyncRoute } from "./routes/clevertap-2-way-apptrove-native-sync";
import { clevertapIntegrationRoute } from "./routes/clevertap-integration";
import { clickInjectionDetectionMobileRoute } from "./routes/click-injection-detection-mobile";
import { clickSpamDetectionRoute } from "./routes/click-spam-detection";
import { clickdealerIntegrationViaApiRoute } from "./routes/clickdealer-integration-via-api";
import { clusterAnalysisGroupLevelAnomalyRoute } from "./routes/cluster-analysis-group-level-anomaly";
import { cohortAnalysisRoute } from "./routes/cohort-analysis";
import { conversionCrudViewEditRejectPermissionScopeRoute } from "./routes/conversion-crud-view-edit-reject-permission-scope";
import { conversionTimingAnalysisRoute } from "./routes/conversion-timing-analysis";
import { cordovaIonicSdkRoute } from "./routes/cordova-ionic-sdk";
import { costEtlImportAdNetworkSpendComputeNetMargiRoute } from "./routes/cost-etl-import-ad-network-spend-compute-net-margi";
import { couponCodeWidgetRoute } from "./routes/coupon-code-widget";
import { cpaSafeguardAttributionFraudProtectionRoute } from "./routes/cpa-safeguard-attribution-fraud-protection";
import { cpaleadIntegrationRoute } from "./routes/cpalead-integration";
import { crOptimizationRuleAutoActionOnCrBelowTargetRoute } from "./routes/cr-optimization-rule-auto-action-on-cr-below-target";
import { creativeAbRoute } from "./routes/creative-ab";
import { affiliateApprovalRoute } from "./routes/affiliate-approval";
import { affiliateReferralRoute } from "./routes/affiliate-referral";
import { affiliateReferralBuilderRoute } from "./routes/affiliate-referral-builder";
import { affiliateSignupRoute } from "./routes/affiliate-signup";
import { agencyDashboardRoute } from "./routes/agency-dashboard";
import { agencyFeaturesRoute } from "./routes/agency-features";
import { bulkCampaignActionsRoute } from "./routes/bulk-campaign-actions";
import { bulkLpUploadRoute } from "./routes/bulk-lp-upload";
import { bulkTargetingRoute } from "./routes/bulk-targeting";
import { campaignDashboardRoute } from "./routes/campaign-dashboard";
import { campaignStatesRoute } from "./routes/campaign-states";
import { campaignTemplatesRoute } from "./routes/campaign-templates";
import { campaignWizardRoute } from "./routes/campaign-wizard";
import { complianceDsrRoute } from "./routes/compliance.dsr";
import { creativeHostingRoute } from "./routes/creative-hosting";
import { creativePreviewRoute } from "./routes/creative-preview";
import { creatorsDrilldownRoute } from "./routes/creators.drilldown";
import { influencersPlatformsRoute } from "./routes/influencers.platforms";
import { smartlinksPubListRoute } from "./routes/smartlinks.pub-list";
import { trackingFlutterSdkRoute } from "./routes/tracking.flutter-sdk";
// Audit+link (5 previously-orphan flat routes; kept alongside namespaced equivalents)
import { ccpaDsrRoute } from "./routes/ccpa-dsr";
import { cookielessFallbackRoute } from "./routes/cookieless-fallback";
import { creativeSizeLimitRoute } from "./routes/creative-size-limit";
import { creativeTypesRoute } from "./routes/creative-types";
import { creatorDrilldownRoute } from "./routes/creator-drilldown";

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: IndexRoute,
});

const mk = (path: string, component: FC) =>
	createRoute({ getParentRoute: () => rootRoute, path, component });

const featureRoutes = [
	mk("/branding", BrandingPage as FC),
	mk("/campaigns", CampaignsIndexPage as FC),
	mk("/campaigns/browser-targeting", BrowserTargetingPage as FC),
	mk("/campaigns/dayparting", DaypartingPage as FC),
	mk("/campaigns/device-targeting", DeviceTargetingPage as FC),
	mk("/campaigns/fallback", FallbackPage as FC),
	mk("/campaigns/geo-targeting", GeoTargetingPage as FC),
	mk("/campaigns/language-targeting", LanguageTargetingPage as FC),
	mk("/campaigns/os-targeting", CampaignsOsTargetingPage as FC),
	mk("/campaigns/spillover", SpilloverPage as FC),
	mk("/campaigns/templates", TemplatesPage as FC),
	mk("/campaigns/tz-scheduling", TzSchedulingPage as FC),
	mk("/campaigns/wizard", WizardPage as FC),
	mk("/creatives", CreativesIndexPage as FC),
	mk("/creatives/size-limit", CreativeSizeLimitPage as FC),
	mk("/creatives/types", CreativeTypesPage as FC),
	mk("/free-trial", FreeTrialPage as FC),
	mk("/pricing", PricingPage as FC),
	mk("/affiliates/approvals", AffiliatesApprovalsPage as FC),
	mk("/creators/promo", CreatorsPromoPage as FC),
	mk("/smartlinks/router", SmartlinksRouterPage as FC),
	mk("/smartlinks/ab", SmartlinksAbPage as FC),
	mk("/smartlinks/autorank", SmartlinksAutorankPage as FC),
	mk("/smartlinks/analytics", SmartlinksAnalyticsPage as FC),
	mk("/smartlinks/fraud", SmartlinksFraudPage as FC),
	mk("/smartlinks/capacity", SmartlinksCapacityPage as FC),
	mk("/smartlinks/targeting", SmartlinkTargetingPage as FC),
	mk("/influencers/link", InfluencersLinkPage as FC),
	mk("/advertisers/portal", AdvertiserPortalPage as FC),
	mk("/trust", TrustCenterPage as FC),
];

// Link-waves + sidebar closeout imports (auditLinkRoutes2 block).
import { abTestingCreativesLandingPagesRoute } from "./routes/ab-testing-creatives-landing-pages";
import { advertiserCapsRoute } from "./routes/advertiser-caps";
import { advertiserCreativeUploadRoute } from "./routes/advertiser-creative-upload";
import { affiliateLeaderboardManualWidgetRoute } from "./routes/affiliate-leaderboard-manual-widget";
import { affiliateReferralProgramBuilderRoute } from "./routes/affiliate-referral-program-builder";
import { agencyDashboardAggregatingAllClientPerformanceRoute } from "./routes/agency-dashboard-aggregating-all-client-performance";
import { agencyPlanUnlockMultiClientSubAdminsWhiteLabelSmartlinkManualCampaignGroupingRoute } from "./routes/agency-plan-unlock-multi-client-sub-admins-white-label-smartlink-manual-campaign-grouping";
import { brandColorLogoUploaderRoute } from "./routes/brand-color-logo-uploader";
import { budgetPacingAlertBurnRateForecastingRoute } from "./routes/budget-pacing-alert-burn-rate-forecasting";
import { bulkImportLostOrdersRoute } from "./routes/bulk-import-lost-orders";
import { bulkSelectBulkActionsRoute } from "./routes/bulk-select-bulk-actions";
import { cakeIntegrationViaApiRoute } from "./routes/cake-integration-via-api";
import { campaignBrowserTargetingRoute } from "./routes/campaign-browser-targeting";
import { campaignCarrierTargetingRoute } from "./routes/campaign-carrier-targeting";
import { campaignDaypartingRoute } from "./routes/campaign-dayparting";
import { campaignDescriptionRoute } from "./routes/campaign-description";
import { campaignDescriptionRichTextEditorRoute } from "./routes/campaign-description-rich-text-editor";
import { campaignDeviceTargetingRoute } from "./routes/campaign-device-targeting";
import { campaignFallbackRoute } from "./routes/campaign-fallback";
import { campaignGeoTargetingRoute } from "./routes/campaign-geo-targeting";
import { campaignLanguageTargetingRoute } from "./routes/campaign-language-targeting";
import { campaignLevelRestrictedCountriesRoute } from "./routes/campaign-level-restricted-countries";
import { campaignOsTargetingRoute } from "./routes/campaign-os-targeting";
import { campaignSpilloverRoute } from "./routes/campaign-spillover";
import { campaignStatusFilterRoute } from "./routes/campaign-status-filter";
import { campaignVisibilityAssignedOnlyAllPerRoleRoute } from "./routes/campaign-visibility-assigned-only-all-per-role";
import { campaignWiseInvoiceGroupingRoute } from "./routes/campaign-wise-invoice-grouping";
import { capHitAutoPauseRoute } from "./routes/cap-hit-auto-pause";
import { capLimitsCampaignPublisherGeoLandingPageLevelsRoute } from "./routes/cap-limits-campaign-publisher-geo-landing-page-levels";
import { capOverDeliveryCancelRoute } from "./routes/cap-over-delivery-cancel";
import { capPeriodsHourlyDailyMonthlyLifetimeRoute } from "./routes/cap-periods-hourly-daily-monthly-lifetime";
import { capTypesApprovedPayoutRevenueClicksConversionPendingRoute } from "./routes/cap-types-approved-payout-revenue-clicks-conversion-pending";
import { carrierIspTargetingRoute } from "./routes/carrier-isp-targeting";
import { clickCountsRoute } from "./routes/click-counts";
import { compositeFraudScoreCombineMultipleVendorScorRoute } from "./routes/composite-fraud-score-combine-multiple-vendor-scor";
import { contestPromotionManagerRoute } from "./routes/contest-promotion-manager";
import { contestsRoute } from "./routes/contests";
import { creativeHostingInsideCampaignRoute } from "./routes/creative-hosting-inside-campaign";
import { creativeMax50mbUploadRoute } from "./routes/creative-max-50mb-upload";
import { creativePreviewUrlRoute } from "./routes/creative-preview-url";
import { creatorPromoRoute } from "./routes/creator-promo";
import { criteoIntegrationRoute } from "./routes/criteo-integration";
import { crossChannelAttributionRoute } from "./routes/cross-channel-attribution";
import { crossDeviceRoute } from "./routes/cross-device";
import { csvXlsxAttachmentOnScheduledReportRoute } from "./routes/csv-xlsx-attachment-on-scheduled-report";
import { customCurrencyPerInvoiceRoute } from "./routes/custom-currency-per-invoice";
import { customExchangeRatePerInvoiceRoute } from "./routes/custom-exchange-rate-per-invoice";
import { customExportScheduleDailyWeeklyCustomIntervaRoute } from "./routes/custom-export-schedule-daily-weekly-custom-interva";
import { customFaviconUploadRoute } from "./routes/custom-favicon-upload";
import { customFxRatePerInvoiceRoute } from "./routes/custom-fx-rate-per-invoice";
import { customLoginDomainRoute } from "./routes/custom-login-domain";
import { customLoginDomainForPublisherPortalRoute } from "./routes/custom-login-domain-for-publisher-portal";
import { customPartnerEmailSenderDomainSmtpRelayRoute } from "./routes/custom-partner-email-sender-domain-smtp-relay";
import { customPartnerEmailSenderDomainSmtpRelayPerRoute } from "./routes/custom-partner-email-sender-domain-smtp-relay-per";
import { customPayoutRulesPerInfluencerTierRoute } from "./routes/custom-payout-rules-per-influencer-tier";
import { customPostbackDomainRoute } from "./routes/custom-postback-domain";
import { customQuotePricingPostStarterRoute } from "./routes/custom-quote-pricing-post-starter";
import { customReportColumnsRoute } from "./routes/custom-report-columns";
import { customTrackingDomainRoute } from "./routes/custom-tracking-domain";
import { customTrackingDomainViaCnameRoute } from "./routes/custom-tracking-domain-via-cname";
import { customUrlSchemeRoute } from "./routes/custom-url-scheme";
import { customerIoIntegrationRoute } from "./routes/customer-io-integration";
import { customizableAlertNotificationTemplateRoute } from "./routes/customizable-alert-notification-template";
import { customizableCampaignAlertsRoute } from "./routes/customizable-campaign-alerts";
import { customizableDashboardsRoute } from "./routes/customizable-dashboards";
import { customizableTableColumnsPerViewRoute } from "./routes/customizable-table-columns-per-view";
import { dashboardExportPngPdfRoute } from "./routes/dashboard-export-png-pdf";
import { dataEncryptionAtRestInTransitRoute } from "./routes/data-encryption-at-rest-in-transit";
import { dataResidencyRoute } from "./routes/data-residency";
import { dataVisibilityToggleAssignedUsersOnlyVsAllRoute } from "./routes/data-visibility-toggle-assigned-users-only-vs-all";
import { datacenterIpDatabaseAwsGcpAzureDoLinodeOvhRoute } from "./routes/datacenter-ip-database-aws-gcp-azure-do-linode-ovh";
import { datacenterTrafficBlockAwsGcpAzureIpBlacklistRoute } from "./routes/datacenter-traffic-block-aws-gcp-azure-ip-blacklist";
import { dateRangeTimezonePickerRoute } from "./routes/date-range-timezone-picker";
import { dayPartingScheduleTargetingRoute } from "./routes/day-parting-schedule-targeting";
import { dayWiseInvoiceGroupingRoute } from "./routes/day-wise-invoice-grouping";
import { defaultCampaignUrlVsPublisherOverrideRoute } from "./routes/default-campaign-url-vs-publisher-override";
import { deferredDeepLinkRoute } from "./routes/deferred-deep-link";
import { delegatedPaymentProcessingRoute } from "./routes/delegated-payment-processing";
import { detailDrawerOverRowRoute } from "./routes/detail-drawer-over-row";
import { deterministicCtvCapiS2sForCtvConversionsRoute } from "./routes/deterministic-ctv-capi-s2s-for-ctv-conversions";
import { deviceBreakdownWidgetRoute } from "./routes/device-breakdown-widget";
import { deviceMaskingDetectionEmulatorModifiedRomRooRoute } from "./routes/device-masking-detection-emulator-modified-rom-roo";
import { digitaloceanSpacesExportCsvJsonRoute } from "./routes/digitalocean-spaces-export-csv-json";
import { disallowedTrafficRoute } from "./routes/disallowed-traffic";
import { disallowedTrafficChannelsPerOfferRoute } from "./routes/disallowed-traffic-channels-per-offer";
import { dpaRoute } from "./routes/dpa";
import { dpaTemplateOnRequestRoute } from "./routes/dpa-template-on-request";
import { dpfRoute } from "./routes/dpf";
import { dragToReorderDashboardLayoutRoute } from "./routes/drag-to-reorder-dashboard-layout";
import { drillDownAnalyticsRoute } from "./routes/drill-down-analytics";
import { duplicateConversionPreventionRoute } from "./routes/duplicate-conversion-prevention";
import { dynamicCommissionByOrderValueAovRoute } from "./routes/dynamic-commission-by-order-value-aov";
import { dynamicCommissionBySkuRoute } from "./routes/dynamic-commission-by-sku";
import { emailInAppWebhookNotificationChannelsRoute } from "./routes/email-in-app-webhook-notification-channels";
import { emailNotificationTemplatesRoute } from "./routes/email-notification-templates";
import { emptyStatesWithCtaRoute } from "./routes/empty-states-with-cta";
import { encryptionPostureRoute } from "./routes/encryption-posture";
import { enterprisePlanRoute } from "./routes/enterprise-plan";
import { enterprisePlanUnlockCsmCustomSlasSslOnDomainCustomDataResidencyRoute } from "./routes/enterprise-plan-unlock-csm-custom-slas-ssl-on-domain-custom-data-residency";
import { entryLevel299MonthRoute } from "./routes/entry-level-299-month";
import { errorStatesRetryButtonRoute } from "./routes/error-states-retry-button";
import { euUsDataPrivacyFrameworkRegistrationRoute } from "./routes/eu-us-data-privacy-framework-registration";
import { eventMatchQualityEmqImprovementViaServerSidRoute } from "./routes/event-match-quality-emq-improvement-via-server-sid";
import { eventTrackingRoute } from "./routes/event-tracking";
import { expoSdkPluginWrapperRoute } from "./routes/expo-sdk-plugin-wrapper";
import { exportPermissionCsvXlsxPerRoleRoute } from "./routes/export-permission-csv-xlsx-per-role";
import { exportToCsvXlsxRoute } from "./routes/export-to-csv-xlsx";
import { fallbackTrafficRulesRoute } from "./routes/fallback-traffic-rules";
import { fasterReportingViaColumnarBackendRoute } from "./routes/faster-reporting-via-columnar-backend";
import { faviconRoute } from "./routes/favicon";
import { fbclidMsclkidTtclidParallelTrackingCrossNetwRoute } from "./routes/fbclid-msclkid-ttclid-parallel-tracking-cross-netw";
import { financeRevenueWidgetRoute } from "./routes/finance-revenue-widget";
import { firstClickAttributionRoute } from "./routes/first-click-attribution";
import { flatFeeCpaCplCpiCpsCpcRevshareHybridPayouRoute } from "./routes/flat-fee-cpa-cpl-cpi-cps-cpc-revshare-hybrid-payou";
import { flatFeePercentageFixedCommissionPayoutsPerInfluencerRoute } from "./routes/flat-fee-percentage-fixed-commission-payouts-per-influencer";
import { fourPayoutTypesDefaultCampaignPublisherSpeciRoute } from "./routes/four-payout-types-default-campaign-publisher-speci";
import { fraudAlertWidgetRoute } from "./routes/fraud-alert-widget";
import { fraudAnalystWorkbenchCaseReviewQueueRoute } from "./routes/fraud-analyst-workbench-case-review-queue";
import { fraudBlacklistSynchronizationAcrossVendorsRoute } from "./routes/fraud-blacklist-synchronization-across-vendors";
import { fraudReportSlicingCampaignPublisherGeoDeviceRoute } from "./routes/fraud-report-slicing-campaign-publisher-geo-device";
import { fraudSpikeAlertsRoute } from "./routes/fraud-spike-alerts";
import { fraudToolAccessPermissionPerRoleRoute } from "./routes/fraud-tool-access-permission-per-role";
import { fraudscoreIntegrationRoute } from "./routes/fraudscore-integration";
import { freeTrialFirst10000ConversionsFree30DaysRoute } from "./routes/free-trial-first-10-000-conversions-free-30-days";
import { fullyCustomizableDashboardWidgetsRoute } from "./routes/fully-customizable-dashboard-widgets";
import { gamificationRoute } from "./routes/gamification";
import { gamificationEnginePointsLevelsBadgesGoalTrackerUiRoute } from "./routes/gamification-engine-points-levels-badges-goal-tracker-ui";
import { gclidOnlyAttributionRoute } from "./routes/gclid-only-attribution";
import { gdprDsrRoute } from "./routes/gdpr-dsr";
import { gdprRegionalDataStorageDsrEndpointsUiHttpApiRoute } from "./routes/gdpr-regional-data-storage-dsr-endpoints-ui-http-api";
import { geoBreakdownWidgetRoute } from "./routes/geo-breakdown-widget";
import { googleAdsEnhancedConversionsServerSideHashedRoute } from "./routes/google-ads-enhanced-conversions-server-side-hashed";
import { googleAdsGclidCaptureRoute } from "./routes/google-ads-gclid-capture";
import { googleAdsGclidParallelTrackingNativeIntegratRoute } from "./routes/google-ads-gclid-parallel-tracking-native-integrat";
import { googleAdsIntegrationRoute } from "./routes/google-ads-integration";
import { hasoffersTuneIntegrationViaApiRoute } from "./routes/hasoffers-tune-integration-via-api";
import { helpCenterRoute } from "./routes/help-center";
import { hourlyDataWidgetClicksConversionsCrEpcCostRoute } from "./routes/hourly-data-widget-clicks-conversions-cr-epc-cost";
import { hubspotIntegrationRoute } from "./routes/hubspot-integration";
import { igamingActivePlayerTrackingRoute } from "./routes/igaming-active-player-tracking";
import { igamingAdminFeesDeductionFromNgrRoute } from "./routes/igaming-admin-fees-deduction-from-ngr";
import { igamingAffiliateLevelNgrGgrFtdReportRoute } from "./routes/igaming-affiliate-level-ngr-ggr-ftd-report";
import { igamingApprovalWorkflowsForPayoutsRoute } from "./routes/igaming-approval-workflows-for-payouts";
import { igamingAuditReadyLogsRoute } from "./routes/igaming-audit-ready-logs";
import { igamingBankTransferEWalletCryptoDisbursementRoute } from "./routes/igaming-bank-transfer-e-wallet-crypto-disbursement";
import { igamingBonusAwardAndWagerThroughTrackingRoute } from "./routes/igaming-bonus-award-and-wager-through-tracking";
import { igamingBonusCostDeductionFromNgrShareRoute } from "./routes/igaming-bonus-cost-deduction-from-ngr-share";
import { igamingBrandLevelReportingIsolationRoute } from "./routes/igaming-brand-level-reporting-isolation";
import { igamingBrandOverridesRoute } from "./routes/igaming-brand-overrides";
import { igamingBrandsRoute } from "./routes/igaming-brands";
import { igamingChargebackDeductionFromAffiliateCommissionRoute } from "./routes/igaming-chargeback-deduction-from-affiliate-commission";
import { igamingChargebackRefundEventsRoute } from "./routes/igaming-chargeback-refund-events";
import { igamingConfigurableDashboardsPerOperatorPartnRoute } from "./routes/igaming-configurable-dashboards-per-operator-partn";
import { igamingCpaRoute } from "./routes/igaming-cpa";
import { igamingCrossBrandPlayerDeDuplicationRoute } from "./routes/igaming-cross-brand-player-de-duplication";
import { igamingCryptoPaymentTrackingUsdtBtcEthOnPlRoute } from "./routes/igaming-crypto-payment-tracking-usdt-btc-eth-on-pl";
import { igamingCryptoPaymentTrackingUsdtBtcEthOnPlayerPipelineRoute } from "./routes/igaming-crypto-payment-tracking-usdt-btc-eth-on-player-pipeline";
import { igamingDepositStreamRoute } from "./routes/igaming-deposit-stream";
import { igamingDepositTrackingRoute } from "./routes/igaming-deposit-tracking";
import { igamingDepositingPlayerCountRoute } from "./routes/igaming-depositing-player-count";
import { igamingFtdEventRoute } from "./routes/igaming-ftd-event";
import { igamingFunnelRoute } from "./routes/igaming-funnel";
import { igamingGdprRegionalDataResidencyForIgamingTRoute } from "./routes/igaming-gdpr-regional-data-residency-for-igaming-t";
import { igamingGeographicJurisdictionBlockGeoExclusioRoute } from "./routes/igaming-geographic-jurisdiction-block-geo-exclusio";
import { igamingHybridRoute } from "./routes/igaming-hybrid";
import { igamingKycHooksExternalForPlayerVettingRoute } from "./routes/igaming-kyc-hooks-external-for-player-vetting";
import { igamingLicenseSpecificDashboardsMgaCuraAoLoRoute } from "./routes/igaming-license-specific-dashboards-mga-cura-ao-lo";
import { igamingMassPayoutProcessingRoute } from "./routes/igaming-mass-payout-processing";
import { igamingMultiCurrencyInvoiceGenerationRoute } from "./routes/igaming-multi-currency-invoice-generation";
import { igamingMultiLevelRoute as igamingMultiLevelRoute_flat } from "./routes/igaming-multi-level";
import { igamingNativeIntegrationsWithSoftswissPragmatRoute } from "./routes/igaming-native-integrations-with-softswiss-pragmat";
import { igamingNegativeCarryoverToggleRoute } from "./routes/igaming-negative-carryover-toggle";
import { igamingNgrGgrRoute } from "./routes/igaming-ngr-ggr";
import { igamingPaymentHoldRoute as igamingPaymentHoldRoute_flat } from "./routes/igaming-payment-hold";
import { igamingPerBrandResponsibleGamblingFiltersRoute } from "./routes/igaming-per-brand-responsible-gambling-filters";
import { igamingPlayerLtvPerAffiliateRoute } from "./routes/igaming-player-ltv-per-affiliate";
import { igamingPlayerLtvPerAffiliateAdvancedRoute } from "./routes/igaming-player-ltv-per-affiliate-advanced";
import { igamingPlayerRegistrationRoute } from "./routes/igaming-player-registration";
import { igamingPlayerSegmentationRoute as igamingPlayerSegmentationRoute_flat } from "./routes/igaming-player-segmentation";
import { igamingPricingRoute } from "./routes/igaming-pricing";
import { igamingPricingSeparateFullyCustomRoute } from "./routes/igaming-pricing-separate-fully-custom";
import { igamingRegulatorAuditExtractMgaUkgcCuraAoFRoute } from "./routes/igaming-regulator-audit-extract-mga-ukgc-cura-ao-f";
import { igamingResponsibleGamblingFiltersSelfExcludedRoute } from "./routes/igaming-responsible-gambling-filters-self-excluded";
import { igamingRetentionChurnByBrandRoute } from "./routes/igaming-retention-churn-by-brand";
import { igamingRevshareRoute } from "./routes/igaming-revshare";
import { igamingS2sPipelineRoute } from "./routes/igaming-s2s-pipeline";
import { igamingScheduledOnDemandPayoutRoute } from "./routes/igaming-scheduled-on-demand-payout";
import { igamingSelfExcludedPlayerFilterNeverCreditARoute } from "./routes/igaming-self-excluded-player-filter-never-credit-a";
import { igamingSimRoute } from "./routes/igaming-sim";
import { igamingSubAffiliateRoute as igamingSubAffiliateRoute_flat } from "./routes/igaming-sub-affiliate";
import { igamingTieredRoute } from "./routes/igaming-tiered";
import { igamingTieredCommissionByNgrFtdWagerThresholdsRoute } from "./routes/igaming-tiered-commission-by-ngr-ftd-wager-thresholds";
import { igamingWagerEventsGranularRoute } from "./routes/igaming-wager-events-granular";
import { igamingWagerTrackingRoute } from "./routes/igaming-wager-tracking";
import { igamingWinLossEventsRoute } from "./routes/igaming-win-loss-events";
import { impactIntegrationViaApiRoute } from "./routes/impact-integration-via-api";
import { impressionTrackingRoute } from "./routes/impression-tracking";
import { inAppFraudRulesRoute } from "./routes/in-app-fraud-rules";
import { indoleadsIntegrationRoute } from "./routes/indoleads-integration";
import { influencerChannelsRoute } from "./routes/influencer-channels";
import { influencerHierarchyRoute } from "./routes/influencer-hierarchy";
import { influencerLinkRoute } from "./routes/influencer-link";
import { influencerPlatformsRoute } from "./routes/influencer-platforms";
import { invalidDeviceIdFilteringRoute } from "./routes/invalid-device-id-filtering";
import { invoiceAttachmentBankDetailsComplianceDocsRoute } from "./routes/invoice-attachment-bank-details-compliance-docs";
import { invoiceCrudPermissionScopeRoute } from "./routes/invoice-crud-permission-scope";
import { invoiceDisputeWorkflowRoute } from "./routes/invoice-dispute-workflow";
import { invoiceNumberSequenceCustomizationRoute } from "./routes/invoice-number-sequence-customization";
import { invoiceStatusDraftSentPaidOverdueRoute } from "./routes/invoice-status-draft-sent-paid-overdue";
import { ios14ServerSideConsentedAttributionPathRoute } from "./routes/ios-14-server-side-consented-attribution-path";
import { iosUniversalLinksRoute } from "./routes/ios-universal-links";
import { ipqsRoute } from "./routes/ipqs";
import { ironsourceIntegrationRoute } from "./routes/ironsource-integration";
import { iterableIntegrationRoute } from "./routes/iterable-integration";
import { itetInstallToEventTimeRuleRoute } from "./routes/itet-install-to-event-time-rule";
import { kochavaGenericPostbackTemplateRoute } from "./routes/kochava-generic-postback-template";
import { kpiInstructionsRoute } from "./routes/kpi-instructions";
import { kpiInstructionsShownToPublisherRoute } from "./routes/kpi-instructions-shown-to-publisher";
import { kpiThresholdTriggersCrDropEpcDropFraudSpikePayoutBRoute } from "./routes/kpi-threshold-triggers-cr-drop-epc-drop-fraud-spike-payout-b";
import { kpiVisibilityPerReportPerRoleRoute } from "./routes/kpi-visibility-per-report-per-role";
import { kycAmlRoute } from "./routes/kyc-aml";
import { kycAmlVettingWorkflowFintechVerticalRoute } from "./routes/kyc-aml-vetting-workflow-fintech-vertical";
import { lastClickAttributionRoute } from "./routes/last-click-attribution";
import { leadOwnershipTransferTrackingSoldToTrackingRoute } from "./routes/lead-ownership-transfer-tracking-sold-to-tracking";
import { leadPingTreePingPostProductizedModuleRoute } from "./routes/lead-ping-tree-ping-post-productized-module";
import { leadValidationLogicNativeNoExternalRequiredRoute } from "./routes/lead-validation-logic-native-no-external-required";
import { leaderboardRoute } from "./routes/leaderboard";
import { legacyMmpIntegrationViaApiRoute } from "./routes/legacy-mmp-integration-via-api";
import { liftoffIntegrationRoute } from "./routes/liftoff-integration";
import { linearAttributionRoute } from "./routes/linear-attribution";
import { linkManagementCrudPermissionScopeRoute } from "./routes/link-management-crud-permission-scope";
import { linkTestRoute } from "./routes/link-test";
import { linkTestToolRoute } from "./routes/link-test-tool";
import { linkedinAdsIntegrationRoute } from "./routes/linkedin-ads-integration";
import { linkedinConversionsApiRoute } from "./routes/linkedin-conversions-api";
import { loadingStatesSkeletonsRoute } from "./routes/loading-states-skeletons";
import { localizedHelpCenterIntercomBackedRoute } from "./routes/localized-help-center-intercom-backed";
import { localizedRegionSpecificDnsCdnEdgeRoute } from "./routes/localized-region-specific-dns-cdn-edge";
import { macroLibraryRoute } from "./routes/macro-library";
import { magentoConnectorRoute } from "./routes/magento-connector";
import { manageCampaignSectionCentralizedDashboardRoute } from "./routes/manage-campaign-section-centralized-dashboard";
import { manualApprovalAffiliateWorkflowRoute } from "./routes/manual-approval-affiliate-workflow";
import { maxbountyIntegrationViaApiRoute } from "./routes/maxbounty-integration-via-api";
import { metaConversionsApiCapiServerSideForwardingRoute } from "./routes/meta-conversions-api-capi-server-side-forwarding";
import { metaFacebookAdsIntegrationRoute } from "./routes/meta-facebook-ads-integration";
import { mfilteritIntegrationRoute } from "./routes/mfilterit-integration";
import { mlBasedInvalidTrafficIvtEngineRoute } from "./routes/ml-based-invalid-traffic-ivt-engine";
import { multiChannelInfluencerCampaignRoiDashboardRoute } from "./routes/multi-channel-influencer-campaign-roi-dashboard";
import { multiClientAgencySupportRoute } from "./routes/multi-client-agency-support";
import { multiLanguagePartnerPortalRoute } from "./routes/multi-language-partner-portal";
import { multipleLandingPagesPerOfferRoute } from "./routes/multiple-landing-pages-per-offer";
import { nativeReconciliationLinkConversionsToStripeBankDepositsRoute } from "./routes/native-reconciliation-link-conversions-to-stripe-bank-deposits";
import { nativeSnowflakeDirectConnectorRoute } from "./routes/native-snowflake-direct-connector";
import { networkApiIntegrationsRoute } from "./routes/network-api-integrations";
import { newFallbackRedirectAttributionRoute } from "./routes/new-fallback-redirect-attribution";
import { nlQueryReportInterfaceAiNativeRoute } from "./routes/nl-query-report-interface-ai-native";
import { noCodeRuleBuilderWithNaturalLanguagePromptsRoute } from "./routes/no-code-rule-builder-with-natural-language-prompts";
import { offerCheckerToolRoute } from "./routes/offer-checker-tool";
import { offer18IntegrationRoute } from "./routes/offer18-integration";
import { onTheGoCampaignMonitoringInMobileAppRoute } from "./routes/on-the-go-campaign-monitoring-in-mobile-app";
import { openapiSpecSdksNodePythonGoPhpRubyRoute } from "./routes/openapi-spec-sdks-node-python-go-php-ruby";
import { opinionUniverseIntegrationRoute } from "./routes/opinion-universe-integration";
import { orderRecoveryWorkflowRoute } from "./routes/order-recovery-workflow";
import { osBreakdownWidgetRoute } from "./routes/os-breakdown-widget";
import { outbrainIntegrationRoute } from "./routes/outbrain-integration";
import { partnerstackIntegrationViaApiRoute } from "./routes/partnerstack-integration-via-api";
import { paymentHoldThresholdMinimumBalanceBeforeReleRoute } from "./routes/payment-hold-threshold-minimum-balance-before-rele";
import { paymentProviderIntegrationForInfluencerPayoutsRoute } from "./routes/payment-provider-integration-for-influencer-payouts";
import { payoutHoldRoute } from "./routes/payout-hold";
import { payoutRecordPullRoute } from "./routes/payout-record-pull";
import { payoutThresholdEnforcementMinBalanceBeforeReRoute } from "./routes/payout-threshold-enforcement-min-balance-before-re";
import { pdfCsvInvoiceExportRoute } from "./routes/pdf-csv-invoice-export";
import { pentestRoute } from "./routes/pentest";
import { perCampaignFraudToggleThresholdSlidersRoute } from "./routes/per-campaign-fraud-toggle-threshold-sliders";
import { perPublisherFraudRuleSetsRoute } from "./routes/per-publisher-fraud-rule-sets";
import { perSubNetworkUserEntitlementsRoute } from "./routes/per-sub-network-user-entitlements";
import { performanceReportPerPublisherRoute } from "./routes/performance-report-per-publisher";
import { performanceTierBadgesForAffiliatesRoute } from "./routes/performance-tier-badges-for-affiliates";
import { permissionScopesLinkPayoutTeamInvoiceAutomatRoute } from "./routes/permission-scopes-link-payout-team-invoice-automat";
import { pinterestAdsIntegrationRoute } from "./routes/pinterest-ads-integration";
import { pinterestConversionsApiRoute } from "./routes/pinterest-conversions-api";
import { pixelCapiDualSendWithDedupViaEventIdRoute } from "./routes/pixel-capi-dual-send-with-dedup-via-event-id";
import { planPauseRoute } from "./routes/plan-pause";
import { planPauseForLowSeasonCustomersRoute } from "./routes/plan-pause-for-low-season-customers";
import { platformFeeBankRoute } from "./routes/platform-fee-bank";
import { platformFeeMonthlyManualBankTransferRoute } from "./routes/platform-fee-monthly-manual-bank-transfer";
import { positionBasedAttributionRoute } from "./routes/position-based-attribution";
import { postAffiliateProIntegrationViaApiRoute } from "./routes/post-affiliate-pro-integration-via-api";
import { postAttributionFraudDetectionRoute } from "./routes/post-attribution-fraud-detection";
import { postbackAsApiEndpointRoute } from "./routes/postback-as-api-endpoint";
import { postbackDomainRoute } from "./routes/postback-domain";
import { preLandingRoute } from "./routes/pre-landing";
import { preLandingPageSupportRoute } from "./routes/pre-landing-page-support";
import { predictiveAnomalyDetectionRoute } from "./routes/predictive-anomaly-detection";
import { prestashopIntegrationViaWebhookRoute } from "./routes/prestashop-integration-via-webhook";
import { pricingPageRoute } from "./routes/pricing-page";
import { proxyTrafficRealTimeDbBlockRoute } from "./routes/proxy-traffic-real-time-db-block";
import { proxyTypeDetectionPublicPrivateResidentialPrRoute } from "./routes/proxy-type-detection-public-private-residential-pr";
import { publicTrustCenterWithAuditorAttestationRoute } from "./routes/public-trust-center-with-auditor-attestation";
import { publicUsageCalculatorRoute } from "./routes/public-usage-calculator";
import { publishedPentestResultsRoute } from "./routes/published-pentest-results";
import { publishedSubProcessorListRoute } from "./routes/published-sub-processor-list";
import { publisherApiRoute } from "./routes/publisher-api";
import { publisherApiToggleRoute } from "./routes/publisher-api-toggle";
import { publisherDashboardSeparatePersonaWithPartnerRoute } from "./routes/publisher-dashboard-separate-persona-with-partner";
import { publisherInvoicingDayWiseOrCampaignWiseRoute } from "./routes/publisher-invoicing-day-wise-or-campaign-wise";
import { publisherSpecificPayoutCrudPermissionScopeRoute } from "./routes/publisher-specific-payout-crud-permission-scope";
import { publisherTrackingDomainsRoute } from "./routes/publisher-tracking-domains";
import { publisherUrlOverrideRoute } from "./routes/publisher-url-override";
import { publisherVsPublisherComparisonWidgetRoute } from "./routes/publisher-vs-publisher-comparison-widget";
import { qrTrackingRoute } from "./routes/qr-tracking";
import { quarterlyAwardRecognitionRoute } from "./routes/quarterly-award-recognition";
import { quarterlyAwardsRoute } from "./routes/quarterly-awards";
import { reactNativeSdkRoute } from "./routes/react-native-sdk";
import { realTimeAnomalyMlCrDropFraudSpikeDepositDropWithSRoute } from "./routes/real-time-anomaly-ml-cr-drop-fraud-spike-deposit-drop-with-s";
import { realTimeConversionNotificationsRoute } from "./routes/real-time-conversion-notifications";
import { realTimeDnsCnamePropagationWithout48hrWaitRoute } from "./routes/real-time-dns-cname-propagation-without-48hr-wait";
import { realTimeEventAlertsConversionChargebackFraudCapHitRoute } from "./routes/real-time-event-alerts-conversion-chargeback-fraud-cap-hit";
import { realTimeEventAlertsModuleRoute } from "./routes/real-time-event-alerts-module";
import { realTimeLinkCouponInteractionMonitoringInfluRoute } from "./routes/real-time-link-coupon-interaction-monitoring-influ";
import { realTimeReportingRoute } from "./routes/real-time-reporting";
import { realTimeRoiMeasurementPerCreatorRoute } from "./routes/real-time-roi-measurement-per-creator";
import { recurringCommissionSaasModelRoute } from "./routes/recurring-commission-saas-model";
import { redditAdsIntegrationRoute } from "./routes/reddit-ads-integration";
import { redditConversionsApiRoute } from "./routes/reddit-conversions-api";
import { referralPartnerRoute } from "./routes/referral-partner";
import { referralPartnerProgramEarnByReferringAccountsRoute } from "./routes/referral-partner-program-earn-by-referring-accounts";
import { regionalDataResidencyIndiaEuUsRoute } from "./routes/regional-data-residency-india-eu-us";
import { regionalDnsRoute } from "./routes/regional-dns";
import { regionalSupportRoute } from "./routes/regional-support";
import { regionalSupportHoursRoute } from "./routes/regional-support-hours";
import { reportAccessByLevelOwnTeamAllTeamPerRoleRoute } from "./routes/report-access-by-level-own-team-all-team-per-role";
import { reportApiRoute } from "./routes/report-api";
import { reportApiCustomColumnsRoute } from "./routes/report-api-custom-columns";
import { restJsonApiFormatRoute } from "./routes/rest-json-api-format";
import { retargetingPixelRoute } from "./routes/retargeting-pixel";
import { retargetingPixelSupportRoute } from "./routes/retargeting-pixel-support";
import { roleTypesDefaultAdvertiserManagerAffiliateMaRoute } from "./routes/role-types-default-advertiser-manager-affiliate-ma";
import { rowActionsMenuEditPauseDuplicateDeleteRoute } from "./routes/row-actions-menu-edit-pause-duplicate-delete";
import { ruleEngineWithIfThenConditionalTriggersRoute } from "./routes/rule-engine-with-if-then-conditional-triggers";
import { salesforceConversionSyncRoute } from "./routes/salesforce-conversion-sync";
import { salesforceHubspotCrmIntegrationRoute } from "./routes/salesforce-hubspot-crm-integration";
import { savedViewsShareableUrlsRoute } from "./routes/saved-views-shareable-urls";
import { scalingPayoutsVolumeBreakpointsRoute } from "./routes/scaling-payouts-volume-breakpoints";
import { scheduleBasedTriggersDayPartingTimezoneAwareRoute } from "./routes/schedule-based-triggers-day-parting-timezone-aware";
import { scheduledEmailReportDailyWeeklyMonthlyRoute } from "./routes/scheduled-email-report-daily-weekly-monthly";
import { scoreBasedRoutingFraudScoreAutoAllowFlagBlRoute } from "./routes/score-based-routing-fraud-score-auto-allow-flag-bl";
import { scrubWorkflowRoute } from "./routes/scrub-workflow";
import { selfServeCreditCardSubscribeRoute } from "./routes/self-serve-credit-card-subscribe";
import { selfSignupFormForAffiliatesBrandableRoute } from "./routes/self-signup-form-for-affiliates-brandable";
import { selfSignupManualApprovalForAdvertiserAccountsRoute } from "./routes/self-signup-manual-approval-for-advertiser-accounts";
import { soc2Iso27001AttestedRoute } from "./routes/soc-2-iso-27001-attested";
import { spilloverHandlingRoute } from "./routes/spillover-handling";
import { starterPlanSingleClientBasicTrackingCappedClicksConvsRoute } from "./routes/starter-plan-single-client-basic-tracking-capped-clicks-convs";
import { studentStartupTierRoute } from "./routes/student-startup-tier";
import { subNetworkCreationRoute } from "./routes/sub-network-creation";
import { thirdPartyFraudIntegrationsMfilteritFraudscorRoute } from "./routes/third-party-fraud-integrations-mfilterit-fraudscor";
import { threeTierPricingStarterAgencyEnterpriseRoute } from "./routes/three-tier-pricing-starter-agency-enterprise";
import { tieredAffiliateOnboardingRoute } from "./routes/tiered-affiliate-onboarding";
import { trackerityReferralPartnerProgramRoute } from "./routes/trackerity-referral-partner-program";
import { verticalAwareFraudRulesIgamingVsFintechVsERoute } from "./routes/vertical-aware-fraud-rules-igaming-vs-fintech-vs-e";
import { volumeBasedPricingPerClickPerConversionRoute } from "./routes/volume-based-pricing-per-click-per-conversion";
import { whiteLabelTrackingDomainCnameWithSslRoute } from "./routes/white-label-tracking-domain-cname-with-ssl";

const wave6Routes: AnyRoute[] = [
	advertisersApprovalRoute,
	advertisersCampaignUploadRoute,
	advertisersCapsRoute,
	advertisersCreativeUploadRoute,
	advertisersNotificationsRoute,
	advertisersPublisherStatsRoute,
	advertisersWidgetsRoute,
	affiliatesGroupsRoute,
	affiliatesManagerRoute,
	affiliatesNotesRoute,
	publishersApiRoute,
	publishersTrackingDomainsRoute,
	publishersPayoutOverridesRoute,
	publishersCapsRoute,
	affiliatesTiersRoute,
	payoutsHoldRoute,
	advertisersSubUsersRoute,
	advertisersInvoicesRoute,
	advertisersRbacRoute,
	conversionsScrubRoute,
	affiliatesReferralProgramRoute,
	publishersApiToggleRoute,
	trackingApiTrackingRoute,
	trackingCrossDeviceRoute,
	trackingEventsRoute,
	trackingFirstPartyCookieRoute,
	trackingIframeGatewayRoute,
	trackingInvitationCodesRoute,
	trackingJsPixelRoute,
	trackingQrRoute,
	trackingS2sPostbackRoute,
	trackingClickCountsRoute,
	igamingPaymentHoldRoute,
	trackingUniversalTagRoute,
	trackingCookielessFallbackRoute,
	trackingCouponsRoute,
	igamingMultiLevelRoute,
	igamingPlayerSegmentationRoute,
	igamingSubAffiliateRoute,
	trackingImpressionsRoute,
	trackingAndroidAppLinksRoute,
	trackingUniversalLinksRoute,
	trackingDeferredDeepLinkRoute,
	trackingPostbackApiRoute,
	trackingMacrosRoute,
	settingsCustomLoginDomainRoute,
	settingsCustomTrackingDomainRoute,
	mountSettingsCustomPostbackDomainRoute(rootRoute),
	trackingDeepLinkRoute,
	campaignsMultiGoalRoute,
	settingsAdminApiRoute,
	trackingSdkCustomParamsRoute,
	settingsAdvertiserApiRoute,
	developersSdkDocsRoute,
	trackingSdkEventsRoute,
	settingsPublisherApiRoute,
	settingsSubnetworkFaviconRoute,
	settingsWebhooksRoute,
	trackingWebToAppRoute,
	trackingAndroidSdkRoute,
	trackingIosSdkRoute,
	offerCheckerRoute,
	linkTesterRoute,
	campaignGroupingRoute,
	campaignCloneRoute,
	publisherCapsRoute,
	conversionScrubRoute,
	affiliateManagerAssignmentRoute,
	couponTrackingRoute,
	clickLogReportRoute,
	publisherPerformanceReportRoute,
	reportExportRoute,
	publisherPayoutOverridesRoute,
	referralCodeTrackingRoute,
	guidedOnboardingRoute,
	customFraudRuleEnginePerCampaignPerPartnerRoute,
	budgetPacingAlertsRoute,
	campaignTzSchedulingRoute,
	savedReportTemplatesRoute,
	sftpScheduledExportRoute,
	igamingScheduledBiWarehouseExportsEtlRoute,
	automationCrudPermissionScopeRoute,
	logLevelDataAccessRoute,
	advertiserPayoutRulesRoute,
	metricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitRoute,
	advertiserSignupRoute,
	advertiserBillingRoute,
	multiCurrencyRoute,
	topAdvertisersWidgetRoute,
	topPublishersWidgetRoute,
	campaignReadEndpointRoute,
	conversionPullEndpointRoute,
	offerPreviewApiRoute,
	clickLevelDetailReportsRoute,
	goalReportsRoute,
	globalGoalTrackingRoute,
	goalProgressWidgetRoute,
	fraudReportsMostFraudulentCampaignsPublishersRoute,
	realTimeClickFraudDetectionRoute,
	realTimeConversionFraudDetectionRoute,
	landingPagesRoute,
	commissionPreviewRoute,
	campaignArchiveRoute,
	lifetimeCommissionRoute,
	customAttributionWindowsRoute,
	advertiserManagerRoute,
];

const wave7Routes: AnyRoute[] = [
	abnormalPatternMlRoute,
	adjustPostbackTemplateRoute,
	admitadIntegrationRoute,
	advertiserCommissionRoute,
	advertiserMacrosRoute,
	aiPoweredFraudAlgorithmsRoute,
	allowedTrafficRoute,
	amazonAdvertisingIntegrationRoute,
	anomalyAlertsMlDrivenUnusualActivityPatternsRoute,
	anonymizerTorBlockRoute,
	anuraIntegrationRoute,
	apiKeyGenerationPermissionPerRoleRoute,
	apiKeyHeaderAuthRoute,
	apiRateLimitingRoute,
	applovinIntegrationRoute,
	appmetricaIntegrationRoute,
	appsflyerIntegrationMmpBridgeRoute,
	SupportSlaRoute,
	advertiserDashboardSeparatePersonaRoute,
	apiDrivenReportPullForCustomBiDashboardsRoute,
];

const wave7cRoutes: AnyRoute[] = [
	advancedRetargetingControlsForSmarterComplianceRoute,
	advertiserApiRoute,
	agencyDashboardAggregatingAllClientPerformancRoute,
	agentDrivenCampaignOptimizationAiNativeRoute,
];

const wave8Routes: AnyRoute[] = [
	appsflyerNetworkPostbackInstallsInAppEventsRoute,
	apptrovePricingRoute,
	auditTrailSecurityComplianceLogRoute,
	autoActionBlockMarkRejectAlertOnFraudRoute,
	autoAlertAffiliateViaEmailInAppRoute,
	autoAlertPublisherManagerEmailRoute,
	autoBlockPublisherWhenFraudThresholdHitRoute,
	autoDebitBillingStripeCardOnFileForPlatformFeesRoute,
	autoPauseCampaignWhenThresholdHitRoute,
	automatedInvoiceGenerationPublisherAdvertiserRoute,
	automaticLetSEncryptRenewalNotManualTicketRoute,
	awinIntegrationApiKeyPublisherIdRoute,
	awsS3DataExportIntegrationRoute,
	awsS3ExportScheduledClickConversionLogsRoute,
	behavioralAnomalyDetectionAbnormalUserActionsRoute,
	bigcommerceIntegrationViaWebhookRoute,
	blacklistIpBlockAnonymizerBlockRoute,
	bonusStructureDisplayForPartnersRoute,
	botTrafficDetectionRoute,
	branchNetworkPartnerPostbackRoute,
	brandedPartnerPortalPerSubNetworkRoute,
	brazeEventSyncIntegrationRoute,
	brazeIterableCustomerIoIntegrationsRoute,
];

const wave9Routes: AnyRoute[] = [
	affiliateApprovalRoute,
	affiliateReferralRoute,
	affiliateReferralBuilderRoute,
	affiliateSignupRoute,
	agencyDashboardRoute,
	agencyFeaturesRoute,
	bulkCampaignActionsRoute,
	bulkLpUploadRoute,
	bulkTargetingRoute,
	campaignDashboardRoute,
	campaignStatesRoute,
	campaignTemplatesRoute,
	campaignWizardRoute,
	complianceDsrRoute,
	creativeHostingRoute,
	creativePreviewRoute,
	creatorsDrilldownRoute,
	influencersPlatformsRoute,
	smartlinksPubListRoute,
	trackingFlutterSdkRoute,
];

const wave9CloseoutRoutes: AnyRoute[] = [
	adminApiRoute,
	advertiserApiV2Route,
	advertiserApprovalRoute,
	advertiserCampaignUploadRoute,
	advertiserInvoicesRoute,
	advertiserNotificationsRoute,
	advertiserPortalRoute,
	advertiserPublisherStatsRoute,
	advertiserRbacRoute,
	advertiserSubUsersRoute,
	advertiserWidgetsRoute,
	affiliateGroupsRoute,
	affiliateManagerRoute,
	affiliateNotesRoute,
	affiliateReferralProgramRoute,
	affiliateTiersRoute,
	agencyRoute,
	androidAppLinksRoute,
	androidSdkRoute,
	apiTrackingRoute,
	capUtilizationWidgetRoute,
	capiEventQualityScoreMonitoringRoute,
];

const wave9Closeout2Routes: AnyRoute[] = [
	capiTestEventModeForVerificationRoute,
	ccSubscribeRoute,
	chartboostIntegrationRoute,
	clevertap2WayApptroveNativeSyncRoute,
	clevertapIntegrationRoute,
	clickInjectionDetectionMobileRoute,
	clickSpamDetectionRoute,
	clickdealerIntegrationViaApiRoute,
	clusterAnalysisGroupLevelAnomalyRoute,
	cohortAnalysisRoute,
	conversionCrudViewEditRejectPermissionScopeRoute,
	conversionTimingAnalysisRoute,
	cordovaIonicSdkRoute,
	costEtlImportAdNetworkSpendComputeNetMargiRoute,
	couponCodeWidgetRoute,
	cpaSafeguardAttributionFraudProtectionRoute,
	cpaleadIntegrationRoute,
	crOptimizationRuleAutoActionOnCrBelowTargetRoute,
	creativeAbRoute,
];

const auditLinkRoutes2: AnyRoute[] = [
	abTestingCreativesLandingPagesRoute,
	advertiserCapsRoute,
	advertiserCreativeUploadRoute,
	affiliateLeaderboardManualWidgetRoute,
	affiliateReferralProgramBuilderRoute,
	agencyDashboardAggregatingAllClientPerformanceRoute,
	agencyPlanUnlockMultiClientSubAdminsWhiteLabelSmartlinkManualCampaignGroupingRoute,
	brandColorLogoUploaderRoute,
	budgetPacingAlertBurnRateForecastingRoute,
	bulkImportLostOrdersRoute,
	bulkSelectBulkActionsRoute,
	cakeIntegrationViaApiRoute,
	campaignBrowserTargetingRoute,
	campaignCarrierTargetingRoute,
	campaignDaypartingRoute,
	campaignDescriptionRoute,
	campaignDescriptionRichTextEditorRoute,
	campaignDeviceTargetingRoute,
	campaignFallbackRoute,
	campaignGeoTargetingRoute,
	campaignLanguageTargetingRoute,
	campaignLevelRestrictedCountriesRoute,
	campaignOsTargetingRoute,
	campaignSpilloverRoute,
	campaignStatusFilterRoute,
	campaignVisibilityAssignedOnlyAllPerRoleRoute,
	campaignWiseInvoiceGroupingRoute,
	capHitAutoPauseRoute,
	capLimitsCampaignPublisherGeoLandingPageLevelsRoute,
	capOverDeliveryCancelRoute,
	capPeriodsHourlyDailyMonthlyLifetimeRoute,
	capTypesApprovedPayoutRevenueClicksConversionPendingRoute,
	carrierIspTargetingRoute,
	clickCountsRoute,
	compositeFraudScoreCombineMultipleVendorScorRoute,
	contestPromotionManagerRoute,
	contestsRoute,
	creativeHostingInsideCampaignRoute,
	creativeMax50mbUploadRoute,
	creativePreviewUrlRoute,
	creatorPromoRoute,
	criteoIntegrationRoute,
	crossChannelAttributionRoute,
	crossDeviceRoute,
	csvXlsxAttachmentOnScheduledReportRoute,
	customCurrencyPerInvoiceRoute,
	customExchangeRatePerInvoiceRoute,
	customExportScheduleDailyWeeklyCustomIntervaRoute,
	customFaviconUploadRoute,
	customFxRatePerInvoiceRoute,
	customLoginDomainRoute,
	customLoginDomainForPublisherPortalRoute,
	customPartnerEmailSenderDomainSmtpRelayRoute,
	customPartnerEmailSenderDomainSmtpRelayPerRoute,
	customPayoutRulesPerInfluencerTierRoute,
	customPostbackDomainRoute,
	customQuotePricingPostStarterRoute,
	customReportColumnsRoute,
	customTrackingDomainRoute,
	customTrackingDomainViaCnameRoute,
	customUrlSchemeRoute,
	customerIoIntegrationRoute,
	customizableAlertNotificationTemplateRoute,
	customizableCampaignAlertsRoute,
	customizableDashboardsRoute,
	customizableTableColumnsPerViewRoute,
	dashboardExportPngPdfRoute,
	dataEncryptionAtRestInTransitRoute,
	dataResidencyRoute,
	dataVisibilityToggleAssignedUsersOnlyVsAllRoute,
	datacenterIpDatabaseAwsGcpAzureDoLinodeOvhRoute,
	datacenterTrafficBlockAwsGcpAzureIpBlacklistRoute,
	dateRangeTimezonePickerRoute,
	dayPartingScheduleTargetingRoute,
	dayWiseInvoiceGroupingRoute,
	defaultCampaignUrlVsPublisherOverrideRoute,
	deferredDeepLinkRoute,
	delegatedPaymentProcessingRoute,
	detailDrawerOverRowRoute,
	deterministicCtvCapiS2sForCtvConversionsRoute,
	deviceBreakdownWidgetRoute,
	deviceMaskingDetectionEmulatorModifiedRomRooRoute,
	digitaloceanSpacesExportCsvJsonRoute,
	disallowedTrafficRoute,
	disallowedTrafficChannelsPerOfferRoute,
	dpaRoute,
	dpaTemplateOnRequestRoute,
	dpfRoute,
	dragToReorderDashboardLayoutRoute,
	drillDownAnalyticsRoute,
	duplicateConversionPreventionRoute,
	dynamicCommissionByOrderValueAovRoute,
	dynamicCommissionBySkuRoute,
	emailInAppWebhookNotificationChannelsRoute,
	emailNotificationTemplatesRoute,
	emptyStatesWithCtaRoute,
	encryptionPostureRoute,
	enterprisePlanRoute,
	enterprisePlanUnlockCsmCustomSlasSslOnDomainCustomDataResidencyRoute,
	entryLevel299MonthRoute,
	errorStatesRetryButtonRoute,
	euUsDataPrivacyFrameworkRegistrationRoute,
	eventMatchQualityEmqImprovementViaServerSidRoute,
	eventTrackingRoute,
	expoSdkPluginWrapperRoute,
	exportPermissionCsvXlsxPerRoleRoute,
	exportToCsvXlsxRoute,
	fallbackTrafficRulesRoute,
	fasterReportingViaColumnarBackendRoute,
	faviconRoute,
	fbclidMsclkidTtclidParallelTrackingCrossNetwRoute,
	financeRevenueWidgetRoute,
	firstClickAttributionRoute,
	flatFeeCpaCplCpiCpsCpcRevshareHybridPayouRoute,
	flatFeePercentageFixedCommissionPayoutsPerInfluencerRoute,
	fourPayoutTypesDefaultCampaignPublisherSpeciRoute,
	fraudAlertWidgetRoute,
	fraudAnalystWorkbenchCaseReviewQueueRoute,
	fraudBlacklistSynchronizationAcrossVendorsRoute,
	fraudReportSlicingCampaignPublisherGeoDeviceRoute,
	fraudSpikeAlertsRoute,
	fraudToolAccessPermissionPerRoleRoute,
	fraudscoreIntegrationRoute,
	freeTrialFirst10000ConversionsFree30DaysRoute,
	fullyCustomizableDashboardWidgetsRoute,
	gamificationRoute,
	gamificationEnginePointsLevelsBadgesGoalTrackerUiRoute,
	gclidOnlyAttributionRoute,
	gdprDsrRoute,
	gdprRegionalDataStorageDsrEndpointsUiHttpApiRoute,
	geoBreakdownWidgetRoute,
	googleAdsEnhancedConversionsServerSideHashedRoute,
	googleAdsGclidCaptureRoute,
	googleAdsGclidParallelTrackingNativeIntegratRoute,
	googleAdsIntegrationRoute,
	hasoffersTuneIntegrationViaApiRoute,
	helpCenterRoute,
	hourlyDataWidgetClicksConversionsCrEpcCostRoute,
	hubspotIntegrationRoute,
	igamingActivePlayerTrackingRoute,
	igamingAdminFeesDeductionFromNgrRoute,
	igamingAffiliateLevelNgrGgrFtdReportRoute,
	igamingApprovalWorkflowsForPayoutsRoute,
	igamingAuditReadyLogsRoute,
	igamingBankTransferEWalletCryptoDisbursementRoute,
	igamingBonusAwardAndWagerThroughTrackingRoute,
	igamingBonusCostDeductionFromNgrShareRoute,
	igamingBrandLevelReportingIsolationRoute,
	igamingBrandOverridesRoute,
	igamingBrandsRoute,
	igamingChargebackDeductionFromAffiliateCommissionRoute,
	igamingChargebackRefundEventsRoute,
	igamingConfigurableDashboardsPerOperatorPartnRoute,
	igamingCpaRoute,
	igamingCrossBrandPlayerDeDuplicationRoute,
	igamingCryptoPaymentTrackingUsdtBtcEthOnPlRoute,
	igamingCryptoPaymentTrackingUsdtBtcEthOnPlayerPipelineRoute,
	igamingDepositStreamRoute,
	igamingDepositTrackingRoute,
	igamingDepositingPlayerCountRoute,
	igamingFtdEventRoute,
	igamingFunnelRoute,
	igamingGdprRegionalDataResidencyForIgamingTRoute,
	igamingGeographicJurisdictionBlockGeoExclusioRoute,
	igamingHybridRoute,
	igamingKycHooksExternalForPlayerVettingRoute,
	igamingLicenseSpecificDashboardsMgaCuraAoLoRoute,
	igamingMassPayoutProcessingRoute,
	igamingMultiCurrencyInvoiceGenerationRoute,
	igamingMultiLevelRoute_flat,
	igamingNativeIntegrationsWithSoftswissPragmatRoute,
	igamingNegativeCarryoverToggleRoute,
	igamingNgrGgrRoute,
	igamingPaymentHoldRoute_flat,
	igamingPerBrandResponsibleGamblingFiltersRoute,
	igamingPlayerLtvPerAffiliateRoute,
	igamingPlayerLtvPerAffiliateAdvancedRoute,
	igamingPlayerRegistrationRoute,
	igamingPlayerSegmentationRoute_flat,
	igamingPricingRoute,
	igamingPricingSeparateFullyCustomRoute,
	igamingRegulatorAuditExtractMgaUkgcCuraAoFRoute,
	igamingResponsibleGamblingFiltersSelfExcludedRoute,
	igamingRetentionChurnByBrandRoute,
	igamingRevshareRoute,
	igamingS2sPipelineRoute,
	igamingScheduledOnDemandPayoutRoute,
	igamingSelfExcludedPlayerFilterNeverCreditARoute,
	igamingSimRoute,
	igamingSubAffiliateRoute_flat,
	igamingTieredRoute,
	igamingTieredCommissionByNgrFtdWagerThresholdsRoute,
	igamingWagerEventsGranularRoute,
	igamingWagerTrackingRoute,
	igamingWinLossEventsRoute,
	impactIntegrationViaApiRoute,
	impressionTrackingRoute,
	inAppFraudRulesRoute,
	indoleadsIntegrationRoute,
	influencerChannelsRoute,
	influencerHierarchyRoute,
	influencerLinkRoute,
	influencerPlatformsRoute,
	invalidDeviceIdFilteringRoute,
	invoiceAttachmentBankDetailsComplianceDocsRoute,
	invoiceCrudPermissionScopeRoute,
	invoiceDisputeWorkflowRoute,
	invoiceNumberSequenceCustomizationRoute,
	invoiceStatusDraftSentPaidOverdueRoute,
	ios14ServerSideConsentedAttributionPathRoute,
	iosUniversalLinksRoute,
	ipqsRoute,
	ironsourceIntegrationRoute,
	iterableIntegrationRoute,
	itetInstallToEventTimeRuleRoute,
	kochavaGenericPostbackTemplateRoute,
	kpiInstructionsRoute,
	kpiInstructionsShownToPublisherRoute,
	kpiThresholdTriggersCrDropEpcDropFraudSpikePayoutBRoute,
	kpiVisibilityPerReportPerRoleRoute,
	kycAmlRoute,
	kycAmlVettingWorkflowFintechVerticalRoute,
	lastClickAttributionRoute,
	leadOwnershipTransferTrackingSoldToTrackingRoute,
	leadPingTreePingPostProductizedModuleRoute,
	leadValidationLogicNativeNoExternalRequiredRoute,
	leaderboardRoute,
	legacyMmpIntegrationViaApiRoute,
	liftoffIntegrationRoute,
	linearAttributionRoute,
	linkManagementCrudPermissionScopeRoute,
	linkTestRoute,
	linkTestToolRoute,
	linkedinAdsIntegrationRoute,
	linkedinConversionsApiRoute,
	loadingStatesSkeletonsRoute,
	localizedHelpCenterIntercomBackedRoute,
	localizedRegionSpecificDnsCdnEdgeRoute,
	macroLibraryRoute,
	magentoConnectorRoute,
	manageCampaignSectionCentralizedDashboardRoute,
	manualApprovalAffiliateWorkflowRoute,
	maxbountyIntegrationViaApiRoute,
	metaConversionsApiCapiServerSideForwardingRoute,
	metaFacebookAdsIntegrationRoute,
	mfilteritIntegrationRoute,
	mlBasedInvalidTrafficIvtEngineRoute,
	multiChannelInfluencerCampaignRoiDashboardRoute,
	multiClientAgencySupportRoute,
	multiLanguagePartnerPortalRoute,
	multipleLandingPagesPerOfferRoute,
	nativeReconciliationLinkConversionsToStripeBankDepositsRoute,
	nativeSnowflakeDirectConnectorRoute,
	networkApiIntegrationsRoute,
	newFallbackRedirectAttributionRoute,
	nlQueryReportInterfaceAiNativeRoute,
	noCodeRuleBuilderWithNaturalLanguagePromptsRoute,
	offerCheckerToolRoute,
	offer18IntegrationRoute,
	onTheGoCampaignMonitoringInMobileAppRoute,
	openapiSpecSdksNodePythonGoPhpRubyRoute,
	opinionUniverseIntegrationRoute,
	orderRecoveryWorkflowRoute,
	osBreakdownWidgetRoute,
	outbrainIntegrationRoute,
	partnerstackIntegrationViaApiRoute,
	paymentHoldThresholdMinimumBalanceBeforeReleRoute,
	paymentProviderIntegrationForInfluencerPayoutsRoute,
	payoutHoldRoute,
	payoutRecordPullRoute,
	payoutThresholdEnforcementMinBalanceBeforeReRoute,
	pdfCsvInvoiceExportRoute,
	pentestRoute,
	perCampaignFraudToggleThresholdSlidersRoute,
	perPublisherFraudRuleSetsRoute,
	perSubNetworkUserEntitlementsRoute,
	performanceReportPerPublisherRoute,
	performanceTierBadgesForAffiliatesRoute,
	permissionScopesLinkPayoutTeamInvoiceAutomatRoute,
	pinterestAdsIntegrationRoute,
	pinterestConversionsApiRoute,
	pixelCapiDualSendWithDedupViaEventIdRoute,
	planPauseRoute,
	planPauseForLowSeasonCustomersRoute,
	platformFeeBankRoute,
	platformFeeMonthlyManualBankTransferRoute,
	positionBasedAttributionRoute,
	postAffiliateProIntegrationViaApiRoute,
	postAttributionFraudDetectionRoute,
	postbackAsApiEndpointRoute,
	postbackDomainRoute,
	preLandingRoute,
	preLandingPageSupportRoute,
	predictiveAnomalyDetectionRoute,
	prestashopIntegrationViaWebhookRoute,
	pricingPageRoute,
	proxyTrafficRealTimeDbBlockRoute,
	proxyTypeDetectionPublicPrivateResidentialPrRoute,
	publicTrustCenterWithAuditorAttestationRoute,
	publicUsageCalculatorRoute,
	publishedPentestResultsRoute,
	publishedSubProcessorListRoute,
	publisherApiRoute,
	publisherApiToggleRoute,
	publisherDashboardSeparatePersonaWithPartnerRoute,
	publisherInvoicingDayWiseOrCampaignWiseRoute,
	publisherSpecificPayoutCrudPermissionScopeRoute,
	publisherTrackingDomainsRoute,
	publisherUrlOverrideRoute,
	publisherVsPublisherComparisonWidgetRoute,
	qrTrackingRoute,
	quarterlyAwardRecognitionRoute,
	quarterlyAwardsRoute,
	reactNativeSdkRoute,
	realTimeAnomalyMlCrDropFraudSpikeDepositDropWithSRoute,
	realTimeConversionNotificationsRoute,
	realTimeDnsCnamePropagationWithout48hrWaitRoute,
	realTimeEventAlertsConversionChargebackFraudCapHitRoute,
	realTimeEventAlertsModuleRoute,
	realTimeLinkCouponInteractionMonitoringInfluRoute,
	realTimeReportingRoute,
	realTimeRoiMeasurementPerCreatorRoute,
	recurringCommissionSaasModelRoute,
	redditAdsIntegrationRoute,
	redditConversionsApiRoute,
	referralPartnerRoute,
	referralPartnerProgramEarnByReferringAccountsRoute,
	regionalDataResidencyIndiaEuUsRoute,
	regionalDnsRoute,
	regionalSupportRoute,
	regionalSupportHoursRoute,
	reportAccessByLevelOwnTeamAllTeamPerRoleRoute,
	reportApiRoute,
	reportApiCustomColumnsRoute,
	restJsonApiFormatRoute,
	retargetingPixelRoute,
	retargetingPixelSupportRoute,
	roleTypesDefaultAdvertiserManagerAffiliateMaRoute,
	rowActionsMenuEditPauseDuplicateDeleteRoute,
	ruleEngineWithIfThenConditionalTriggersRoute,
	salesforceConversionSyncRoute,
	salesforceHubspotCrmIntegrationRoute,
	savedViewsShareableUrlsRoute,
	scalingPayoutsVolumeBreakpointsRoute,
	scheduleBasedTriggersDayPartingTimezoneAwareRoute,
	scheduledEmailReportDailyWeeklyMonthlyRoute,
	scoreBasedRoutingFraudScoreAutoAllowFlagBlRoute,
	scrubWorkflowRoute,
	selfServeCreditCardSubscribeRoute,
	selfSignupFormForAffiliatesBrandableRoute,
	selfSignupManualApprovalForAdvertiserAccountsRoute,
	soc2Iso27001AttestedRoute,
	spilloverHandlingRoute,
	starterPlanSingleClientBasicTrackingCappedClicksConvsRoute,
	studentStartupTierRoute,
	subNetworkCreationRoute,
	thirdPartyFraudIntegrationsMfilteritFraudscorRoute,
	threeTierPricingStarterAgencyEnterpriseRoute,
	tieredAffiliateOnboardingRoute,
	trackerityReferralPartnerProgramRoute,
	verticalAwareFraudRulesIgamingVsFintechVsERoute,
	volumeBasedPricingPerClickPerConversionRoute,
	whiteLabelTrackingDomainCnameWithSslRoute,
];

const auditLinkRoutes: AnyRoute[] = [
	ccpaDsrRoute,
	cookielessFallbackRoute,
	creativeSizeLimitRoute,
	creativeTypesRoute,
	creatorDrilldownRoute,
];

const routeTree = rootRoute.addChildren([
	indexRoute,
	...featureRoutes,
	...wave6Routes,
	...wave7Routes,
	...wave7cRoutes,
	...wave8Routes,
	...wave9Routes,
	...wave9CloseoutRoutes,
	...wave9Closeout2Routes,
	...auditLinkRoutes,
	...auditLinkRoutes2,
]);

export const router = createRouter({
	routeTree,
	defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
